import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';

const playfairFont = "'Playfair Display', serif";

export const ProductCard = ({ product, showAddToCart = true }) => {
  const navigate = useNavigate();
  const hasVariants = Array.isArray(product?.variants) && product.variants.length > 0;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (hasVariants) {
      navigate(`/products/${product.id}`);
      return;
    }
    try {
      await axios.post(`${API}/cart/add`, { product_id: product.id, quantity: 1 }, { withCredentials: true });
      toast.success('Added to cart!');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please sign in first');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to add to cart');
      }
    }
  };

  const handleCardClick = () => {
    navigate(`/products/${product.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8 }}
      transition={{ duration: 0.3 }}
      onClick={handleCardClick}
      className="group relative rounded-2xl overflow-hidden backdrop-blur-xl cursor-pointer"
      style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden" style={{ background: 'rgba(0, 0, 0, 0.2)' }}>
        <img
          src={(product.images && product.images[0]) || product.image_url}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="%23202824" width="200" height="200"/><text x="100" y="100" fill="%23A3B8AC" font-family="sans-serif" font-size="13" text-anchor="middle" dy=".3em">No image</text></svg>';
          }}
        />
        {product.is_flash_sale && (
          <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-medium backdrop-blur-md" style={{ background: 'rgba(212, 175, 55, 0.9)', color: '#080C0A' }} data-testid="flash-sale-badge">
            FLASH SALE
          </div>
        )}
        {product.discount_percent > 0 && (
          <div className="absolute top-4 right-4 px-2 py-1 rounded-lg text-xs font-bold" style={{ background: 'rgba(239, 68, 68, 0.9)', color: '#FFF' }}>
            -{product.discount_percent}%
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-6">
        <h3 className="font-medium text-base mb-3 line-clamp-2 leading-snug" style={{ color: '#F8FAF9' }} data-testid="product-name">
          {product.name}
        </h3>
        
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-xl font-semibold" style={{ color: '#D4AF37', fontFamily: playfairFont }} data-testid="product-price">{formatPrice(product.price)}</span>
          {product.original_price && product.original_price > product.price && (
            <span className="text-sm line-through" style={{ color: '#A3B8AC' }}>{formatPrice(product.original_price)}</span>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs" style={{ color: '#A3B8AC' }} data-testid="product-stock">
            {hasVariants ? `${product.variants.length} varian` : `Stock: ${product.stock}`}
          </span>
          {showAddToCart && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAddToCart}
              className="p-2.5 rounded-xl transition-all duration-300"
              style={{ background: 'rgba(212, 175, 55, 0.2)', border: '1px solid rgba(212, 175, 55, 0.3)' }}
              data-testid={hasVariants ? 'select-variant-btn' : 'add-to-cart-btn'}
              title={hasVariants ? 'Pilih varian' : 'Tambah ke keranjang'}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#D4AF37';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(212, 175, 55, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <ShoppingCart className="w-5 h-5" style={{ color: '#D4AF37' }} />
            </motion.button>
          )}
        </div>
      </div>

      {/* Hover glow effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ boxShadow: '0 8px 32px rgba(212, 175, 55, 0.15)' }} />
    </motion.div>
  );
};